import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface FilterBarProps {
  dosageRange: string;
  studyPopulation: string;
  duration: string;
  studyType: string;
  qualityScore: string;
  yearRange: string;
  sampleSize: string;
  onDosageChange: (value: string) => void;
  onPopulationChange: (value: string) => void;
  onDurationChange: (value: string) => void;
  onStudyTypeChange: (value: string) => void;
  onQualityScoreChange: (value: string) => void;
  onYearRangeChange: (value: string) => void;
  onSampleSizeChange: (value: string) => void;
  onClearAll: () => void;
}

export default function FilterBar({
  dosageRange,
  studyPopulation,
  duration,
  studyType,
  qualityScore,
  yearRange,
  sampleSize,
  onDosageChange,
  onPopulationChange,
  onDurationChange,
  onStudyTypeChange,
  onQualityScoreChange,
  onYearRangeChange,
  onSampleSizeChange,
  onClearAll
}: FilterBarProps) {
  const activeFilterCount = [
    dosageRange !== "any",
    studyPopulation !== "any",
    duration !== "any",
    studyType !== "all",
    qualityScore !== "all",
    yearRange !== "all",
    sampleSize !== "any"
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        {/* Dosage Range */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Dosage Range</label>
          <select
            value={dosageRange}
            onChange={(e) => onDosageChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="any">Any Dosage</option>
            <option value="<500mg">&lt;500mg</option>
            <option value="500-1000mg">500-1000mg</option>
            <option value="1-3g">1-3g</option>
            <option value=">3g">&gt;3g</option>
          </select>
        </div>

        {/* Study Population */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Study Population</label>
          <select
            value={studyPopulation}
            onChange={(e) => onPopulationChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="any">Any Population</option>
            <option value="healthy">Healthy</option>
            <option value="diseased">Diseased</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>

        {/* Duration */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Study Duration</label>
          <select
            value={duration}
            onChange={(e) => onDurationChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="any">Any Duration</option>
            <option value="<4weeks">&lt;4 weeks</option>
            <option value="4-12weeks">4-12 weeks</option>
            <option value=">12weeks">&gt;12 weeks</option>
          </select>
        </div>

        {/* Study Type */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Study Type</label>
          <select
            value={studyType}
            onChange={(e) => onStudyTypeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="all">All Types</option>
            <option value="RCT">RCT</option>
            <option value="Observational">Observational</option>
            <option value="Meta-analysis">Meta-analysis</option>
            <option value="Systematic Review">Systematic Review</option>
          </select>
        </div>

        {/* Quality Score */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Quality Score</label>
          <select
            value={qualityScore}
            onChange={(e) => onQualityScoreChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="all">All Quality Levels</option>
            <option value="5">⭐⭐⭐⭐⭐ (5 stars)</option>
            <option value="4">⭐⭐⭐⭐ (4+ stars)</option>
            <option value="3">⭐⭐⭐ (3+ stars)</option>
          </select>
        </div>

        {/* Year Range */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Publication Year</label>
          <select
            value={yearRange}
            onChange={(e) => onYearRangeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="all">All Years</option>
            <option value="2020-2025">2020-2025 (Recent)</option>
            <option value="2015-2019">2015-2019</option>
            <option value="2010-2014">2010-2014</option>
            <option value="2000-2009">2000-2009</option>
            <option value="<2000">Before 2000</option>
          </select>
        </div>

        {/* Sample Size */}
        <div>
          <label className="text-sm font-medium mb-2 block text-gray-700">Sample Size</label>
          <select
            value={sampleSize}
            onChange={(e) => onSampleSizeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A98A2C] focus:border-transparent text-sm"
          >
            <option value="any">Any Size</option>
            <option value="<50">&lt;50 participants</option>
            <option value="50-100">50-100 participants</option>
            <option value="100-500">100-500 participants</option>
            <option value=">500">&gt;500 participants</option>
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="secondary" className="text-sm">
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-8 text-sm gap-1"
          >
            <X className="w-4 h-4" />
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
